
import { useState } from 'react';
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

type Props = {
  title: string;
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
};

const KEY_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ñ'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M', '-', '/', '.'],
];

export default function ProductionOperatorInlineTextKeyboard({
  title,
  value,
  onChange,
  onClose,
}: Props) {
  const [uppercase, setUppercase] = useState(true);

  const appendCharacter = (character: string) => {
    const nextCharacter = uppercase
      ? character
      : character.toLowerCase();

    onChange(`${value}${nextCharacter}`);
  };

  const keySx = {
    minWidth: 0,
    minHeight: 46,
    p: 0.5,
    borderRadius: 1.5,
    borderColor: 'rgba(148,163,184,0.22)',
    bgcolor: 'rgba(30,41,59,0.72)',
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 850,
    '&:hover': {
      bgcolor: 'rgba(51,65,85,0.9)',
      borderColor: '#38bdf8',
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(56,189,248,0.3)',
        bgcolor: '#0f172a',
        p: 1.5,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Teclado PROA · {title}
          </Typography>

          <Button size="small" onClick={onClose}>
            Terminar
          </Button>
        </Stack>

        <Box
          sx={{
            borderRadius: 2,
            bgcolor: 'rgba(2,6,23,0.6)',
            border: '1px solid rgba(56,189,248,0.2)',
            px: 1.5,
            py: 1,
            minHeight: 49,
          }}
        >
          <Typography
            sx={{
              color: '#f8fafc',
              fontSize: '1.25rem',
              fontWeight: 900,
              overflowWrap: 'anywhere',
            }}
          >
            {value || '—'}
          </Typography>
        </Box>

        {KEY_ROWS.map((row, index) => (
          <Box
            key={index}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
              gap: 0.5,
            }}
          >
            {row.map((key) => (
              <Button
                key={key}
                variant="outlined"
                onClick={() => appendCharacter(key)}
                sx={keySx}
              >
                {uppercase ? key : key.toLowerCase()}
              </Button>
            ))}
          </Box>
        ))}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 3fr repeat(4, minmax(0, 1fr))',
            gap: 0.5,
          }}
        >
          <Button
            variant={uppercase ? 'contained' : 'outlined'}
            onClick={() => setUppercase((current) => !current)}
            sx={{ ...keySx, fontSize: '0.75rem' }}
          >
            Mayús
          </Button>

          <Button
            variant="outlined"
            onClick={() => onChange(`${value} `)}
            sx={{ ...keySx, fontSize: '0.8rem' }}
          >
            Espacio
          </Button>

          {['_', '@', '#'].map((key) => (
            <Button
              key={key}
              variant="outlined"
              onClick={() => onChange(`${value}${key}`)}
              sx={keySx}
            >
              {key}
            </Button>
          ))}

          <Button
            variant="outlined"
            aria-label="Borrar último carácter"
            onClick={() => onChange(value.slice(0, -1))}
            sx={keySx}
          >
            <BackspaceRoundedIcon />
          </Button>
        </Box>

        <Button
          size="small"
          onClick={() => onChange('')}
          sx={{ color: '#94a3b8', fontWeight: 800 }}
        >
          Borrar todo
        </Button>
      </Stack>
    </Paper>
  );
}
